import { useEffect, useMemo, useState } from "react";
import {
  useFieldApi,
  useFormApi,
} from "@data-driven-forms/react-form-renderer";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import { COUNTRIES } from "../../../constants/countries.c";
import {
  DEFAULT_PHONE_MAX_LENGTH,
  PHONE_COUNTRY_DATA,
} from "../../../constants/phone-countries.c";
import { FORM_FIELD_NAMES } from "../../../constants/form-field-names";
import { countriesValueToBeShownOnTop } from "../../../constants/common.c";
import { isJapanese } from "../../hooks/isJapanese";
import { InputStatusIcon } from "./InputStatusIcon";

interface PhoneCountryOption {
  value: string;
  name: string;
  dialCode: string;
  label: string;
}

const DasPhoneField = (props: any) => {
  const {
    customProp,
    label,
    input,
    inputLabel,
    placeholder,
    isRequired,
    meta: { error, touched },
    FieldArrayProvider,
    dataType,
    ...rest
  } = useFieldApi(props);
  const formApi = useFormApi();
  const { t } = useTranslation();
  const japaneseLang = isJapanese();

  const options: PhoneCountryOption[] = useMemo(() => {
    const list = COUNTRIES.filter(
      (country) => PHONE_COUNTRY_DATA[country.value]
    ).map((country) => {
      const name = t(`countryNames.${country.label}`);
      const dialCode = PHONE_COUNTRY_DATA[country.value].dialCode;
      return {
        value: country.value,
        name,
        dialCode,
        label: `${name} (+${dialCode})`,
      };
    });
    if (!japaneseLang) return list;
    return [
      ...list.filter((item) => countriesValueToBeShownOnTop.includes(item.value)),
      ...list.filter((item) => !countriesValueToBeShownOnTop.includes(item.value)),
    ];
  }, [t, japaneseLang]);

  const [selectedIso, setSelectedIso] = useState<string>(() => {
    const { values, initialValues } = formApi.getState();
    const current = values?.[FORM_FIELD_NAMES.PHONE_COUNTRY_CODE];
    if (current && PHONE_COUNTRY_DATA[current]) return current;
    // Default to the country supplied during link generation
    const linkCountry =
      initialValues?.country?.value || values?.country?.value;
    return linkCountry && PHONE_COUNTRY_DATA[linkCountry] ? linkCountry : "";
  });

  useEffect(() => {
    formApi.change(FORM_FIELD_NAMES.PHONE_COUNTRY_CODE, selectedIso || undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIso]);

  const countryData = PHONE_COUNTRY_DATA[selectedIso];
  const maxDigits = countryData?.maxLength ?? DEFAULT_PHONE_MAX_LENGTH;

  const capFor = (digits: string) =>
    countryData?.nationalPrefix && digits.startsWith(countryData.nationalPrefix)
      ? maxDigits + 1 // allow the customary trunk digit, e.g. 090... in Japan
      : maxDigits;

  const handlePhoneChange = (event: any) => {
    const digits = event.target.value.replace(/[^0-9]/g, "");
    input.onChange(digits.slice(0, capFor(digits)));
  };

  const filterOption = (option: any, rawInput: string) => {
    if (!rawInput) return true;
    const search = rawInput.toLowerCase().trim();
    const dialSearch = search.replace(/^\+/, "");
    const { name, dialCode, value } = option.data as PhoneCountryOption;
    return (
      name.toLowerCase().includes(search) ||
      value.toLowerCase() === search ||
      (/^\d+$/.test(dialSearch) && dialCode.startsWith(dialSearch))
    );
  };

  const input_Label = t(inputLabel);
  return (
    <div className="inputgroup phone-inputgroup">
      {input_Label && (
        <div className="flex-row">
          <label className="input-label">{input_Label}</label>
        </div>
      )}
      <div
        className={
          error && touched ? "phone-field-row phone-row-error" : "phone-field-row"
        }
      >
        <Select
          className="phone-code-select"
          classNamePrefix="phone-code"
          id={FORM_FIELD_NAMES.PHONE_COUNTRY_CODE}
          placeholder="+"
          options={options}
          value={options.find((option) => option.value === selectedIso) || null}
          onChange={(option: any) => setSelectedIso(option?.value || "")}
          filterOption={filterOption}
          formatOptionLabel={(option: any, { context }: any) =>
            context === "value"
              ? `+${option.dialCode}`
              : `${option.name} (+${option.dialCode})`
          }
          noOptionsMessage={() => t("merchant.Billing_Information.fields.NoCountryFound")}
        />
        <span className={error && touched ? "errors-class" : ""}>
          <input
            id={input.name}
            {...input}
            {...rest}
            type="tel"
            inputMode="numeric"
            autoComplete="off"
            maxLength={capFor(String(input.value || ""))}
            onChange={handlePhoneChange}
            placeholder={t(placeholder)}
          />
          <span className="inputcheck">
            <InputStatusIcon
              touched={touched}
              error={error}
              value={input.value}
            />
          </span>
        </span>
      </div>
    </div>
  );
};

export default DasPhoneField;
